import React from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';

import { CaraWorkspacePreset } from '~/api/graphql';
import { useCasesInFamily } from '~/components/home/use-case-catalog';
import { UseCaseChip } from '~/components/home/use-case-tile';

/**
 * The neutral fallback chip rendered when a workspace has no preset (or an
 * unrecognized one). Matches the visual treatment of `UseCaseChip` so the top
 * bar reads consistently.
 */
export const FALLBACK_PRESET_ICON_CHIP: React.ReactNode = (
  <span className="inline-flex rounded p-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300">
    <SparklesIcon className="size-4" />
  </span>
);

/**
 * Resolves a workspace's preset to the chip the home page gives its use cases.
 * A preset is a family of use cases that share a category, and the category is
 * what colors the chip, so the first use case in the family stands for all of
 * them.
 *
 * Returns the sparkle fallback for a missing preset, an unknown preset, or a
 * preset whose use cases are all unavailable.
 */
export const getPresetIconChip = (
  preset: CaraWorkspacePreset | null | undefined
): React.ReactNode => {
  if (!preset) return FALLBACK_PRESET_ICON_CHIP;
  const [first] = useCasesInFamily(preset);
  return first ? <UseCaseChip useCase={first} /> : FALLBACK_PRESET_ICON_CHIP;
};
